import React, { useId } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import CustomSelect from "../../custom/custom-select";
import CustomSelectMulti from "../../custom/custom-select/CustomSelectMulti";
import CustomInput from "../../custom/custom-input/CustomInput";
import OrdersDatePickerTender from "../date-picker-tender/OrdersDatePickerTender";
import { CURRENCIES } from "../../../mock";
import { ASSIGNMENT_METHODS } from "../../../pages/orders/utils/newOrderPayload";
import { CreationCard, CreationToggle } from "./OrderCreationControls";
import OrderCreationTenderIcon from "../../shared/icons/OrderCreationTenderIcon";
import OrderCreationAuctionIcon from "../../shared/icons/OrderCreationAuctionIcon";
import OrderCreationDirectIcon from "../../shared/icons/OrderCreationDirectIcon";
import styles from "./UnifiedOrderCreation.module.scss";

const ICONS = {
  Tender: OrderCreationTenderIcon,
  Auction: OrderCreationAuctionIcon,
  DirectAssignment: OrderCreationDirectIcon,
};

export default function UnifiedCarrierSelection(p) {
  const { t } = useTranslation();
  const user = useSelector((state) => state.user);
  const { errors = {} } = p;
  const direct = p.assignmentMethod === "DirectAssignment";
  const copyCountId = useId();
  const copyCount = p.copyCount ?? 1;
  const changeCopyCount = (value) => {
    if (p.isEdit) return;
    p.setCopyCount(value === "" ? "" : Math.min(15, Math.max(1, Math.trunc(Number(value)) || 1)));
  };
  const currencies = Object.values(CURRENCIES).map((item) => ({
    id: item.code,
    name: item.symbol === item.code ? item.code : `${item.symbol} ${item.code}`,
  }));
  const currency = p.acceptedCurrency;
  const insuranceCost = p.insurance ? Number(p.insurancePrice || 0) : 0;
  const totalPrice = Number(p.price || 0) + (currency === "KZT" ? insuranceCost : 0);
  const totalLabel =
    `${totalPrice.toLocaleString()} ${CURRENCIES[currency]?.symbol || ""}` +
    (insuranceCost && currency !== "KZT" ? ` + ${insuranceCost.toLocaleString()} ₸` : "");
  const selectMethod = (method) => {
    if (p.isEdit) return;
    const permission = method === "DirectAssignment" ? "expeditor_view" : "tender_view";
    if (!user?.permissions?.includes(permission)) {
      toast.info(t("common.noAccessSupportMessage"));
      return;
    }
    p.setAssignmentMethod(method);
  };
  const row = (label, value, total = false) => (
    <div className={`${styles.summaryRow} ${total ? styles.total : ""}`}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
  const currencySelect = (
    <CustomSelect
      labelText={t("unifiedOrder.currencyLabel")}
      placeholder={t("ordersCreateDesktop.paymentSection.selectCurrency")}
      options={currencies}
      defaultValue={currencies.find((item) => item.id === currency)}
      onSubmit={p.setAcceptedCurrency}
      errorText={errors.acceptedCurrency}
    />
  );

  return (
    <CreationCard title={t("unifiedOrder.carrierTitle")} counter={4 + p.points.length}>
      <div className={styles.methods} role="radiogroup" aria-label={t("unifiedOrder.assignmentMethod")}>
        {ASSIGNMENT_METHODS.map((method) => {
          const Icon = ICONS[method];
          return (
            <label className={styles.method} key={method}>
              <span className={styles.methodTop}>
                <Icon aria-hidden="true" className={styles.icon} />
                <input
                  type="radio"
                  name="assignmentMethod"
                  value={method}
                  checked={p.assignmentMethod === method}
                  disabled={p.isEdit}
                  onChange={() => selectMethod(method)}
                  aria-label={t(`unifiedOrder.methods.${method}.title`)}
                />
              </span>
              <strong>{t(`unifiedOrder.methods.${method}.title`)}</strong>
              <small>{t(`unifiedOrder.methods.${method}.description`)}</small>
            </label>
          );
        })}
      </div>
      {errors.assignmentMethod && <span className={styles.error}>{errors.assignmentMethod}</span>}
      {direct ? (
        <CustomSelect
          labelText={t("unifiedOrder.assignTo")}
          placeholder={t("ordersCreateDesktop.paymentSection.choseCompany")}
          options={p.partners}
          defaultValue={p.partner}
          onSubmit={p.setPartner}
          returnObject
          errorText={errors.partner}
        />
      ) : (
        <CustomSelectMulti
          keyId="expeditorCompanyId"
          labelText={t(`unifiedOrder.invite.${p.assignmentMethod}`)}
          placeholder={t("ordersCreateDesktop.paymentSection.choseCompany")}
          options={p.partners}
          defaultValue={p.partnersForTender}
          protectDefaultValues={p.isEdit}
          onSubmit={p.setPartnersForTender}
          returnObject
          isMulti
          errorText={errors.partnersForTender}
        />
      )}

      {direct ? (
        <div className={styles.fields}>
          <CustomInput
            labelText={t("unifiedOrder.transportPrice")}
            placeholder={t("unifiedOrder.pricePlaceholder")}
            inputType="number"
            value={p.price || ""}
            onChangeValue={p.onPriceChange}
            isBgGray
            errorText={errors.price}
          />
          {currencySelect}
        </div>
      ) : (
        <div className={styles.deadline}>
          <OrdersDatePickerTender
            date={p.tenderDateEnd}
            timeEnd={p.tenderEndTime}
            minDate={p.minPickDate}
            isEditable
            setDate={p.setTenderDateEnd}
            setTimeEnd={p.setTenderEndTime}
            labelText={t("unifiedOrder.bidsUntil")}
            labelTimeEnd={t("ordersCreateDesktop.fromSection.timeStartLabel")}
            placeholderText={t("ordersCreateDesktop.fromSection.datePlaceholder")}
            errorDate={errors.tenderDateEnd}
            errortimeEnd={errors.tenderEndTime}
          />
          {currencySelect}
        </div>
      )}
      
      {direct && p.showCopyCount !== false && (
        <div className={styles.groupFields}>
          <div className={styles.toggleText}>
            <label htmlFor={copyCountId}>{t("ordersCreateDesktop.paymentSection.orderDuplicate")}</label>
            <small>{t("ordersCreateDesktop.paymentSection.orderDuplicateQuantity")}</small>
          </div>
          <div className={styles.counterField}>
            <div className={styles.counter}>
              <button
                type="button"
                aria-label={t("complexTender.create.decreaseCount")}
                disabled={p.isEdit || Number(copyCount) <= 1}
                onClick={() => changeCopyCount(Number(copyCount) - 1)}
              >
                −
              </button>
              <input
                id={copyCountId}
                type="number"
                min="1"
                max="15"
                step="1"
                value={copyCount}
                disabled={p.isEdit}
                onChange={(event) => changeCopyCount(event.target.value)}
                onBlur={() => { if (copyCount === "") changeCopyCount(1); }}
                aria-invalid={Boolean(errors.copyCount)}
                aria-describedby={errors.copyCount ? `${copyCountId}-error` : undefined}
              />
              <button
                type="button"
                aria-label={t("complexTender.create.increaseCount")}
                disabled={p.isEdit || Number(copyCount) >= 15}
                onClick={() => changeCopyCount(Number(copyCount) + 1)}
              >
                +
              </button>
            </div>
            {errors.copyCount && <span id={`${copyCountId}-error`} className={styles.error}>{errors.copyCount}</span>}
          </div>
        </div>
      )}

      {p.assignmentMethod === "Auction" && (
        <CustomInput
          isBgGray
          inputType="number"
          labelText={t("unifiedOrder.bidStep", { currency: CURRENCIES[currency]?.symbol || "" })}
          placeholder={t("unifiedOrder.bidStepPlaceholder")}
          value={p.auctionStep}
          onChangeValue={p.setAuctionStep}
          errorText={errors.auctionStep}
        />
      )}
      <div className={styles.carrierOptions}>
        {p.assignmentMethod === "Auction" && (
          <CreationToggle
            title={t("unifiedOrder.extendTitle")}
            description={t("unifiedOrder.extendDescription")}
            checked={p.extendAuction}
            onChange={p.setExtendAuction}
          />
        )}
        {!p.isRailway && p.showDriverAssignment !== false && (
          <CreationToggle
            title={t("ordersCreateDesktop.paymentSection.skipDriverAssignment")}
            description={t("ordersCreateDesktop.paymentSection.orderWithoutDriverAssignment")}
            checked={p.isWithoutExecutor}
            onChange={p.setIsWithoutExecutor}
            disabled={["railway_shipment", "railway_shipment_40"].includes(p.type?.value)}
          />
        )}
        <div className={styles.summary}>
          {row(t("unifiedOrder.assignmentMethod"), t(`unifiedOrder.methods.${p.assignmentMethod}.title`))}
          {p.isComplexTender &&
            row(t("unifiedOrder.groupTitle"), t(p.isRailway ? "railwayOrder.wagons" : "unifiedOrder.vehicles", {
              count: Number(p.vehiclesCount),
            }))}
          {p.insurance && row(t("unifiedOrder.insurance"), `${insuranceCost.toLocaleString()} ₸`)}
          {direct && row(t("ordersCreateDesktop.paymentSection.totalPrice"), totalLabel, true)}
        </div>
      </div>
    </CreationCard>
  );
}
